import { Search, Wrench, LineChart, MessageSquare, ShieldCheck, Home as HomeIcon, FileText, Megaphone } from "lucide-react";
import { Reveal, StaggerGroup, staggerItem } from "../components/Motion";
import { motion } from "framer-motion";
import { PageHero } from "../components/PageHero";
import { CTASection } from "../components/CTASection";
import { Seo } from "../components/Seo";

const services = [
  { icon: Search, t: "Tenant Screening", d: "Comprehensive credit, criminal, eviction history and income verification. We place tenants who pay on time and stay long-term." },
  { icon: Megaphone, t: "Marketing & Leasing", d: "Professional photography, syndicated listings across major platforms, coordinated showings, and airtight lease execution." },
  { icon: Wrench, t: "Maintenance & Repairs", d: "24/7 emergency response and a network of vetted, licensed local vendors at negotiated rates." },
  { icon: LineChart, t: "Financial Reporting", d: "Owner portal with monthly statements, real-time expense tracking, and year-end tax-ready documents." },
  { icon: MessageSquare, t: "Owner & Tenant Relations", d: "A dedicated manager for every owner and same-day responses that keep everyone happy." },
  { icon: ShieldCheck, t: "Compliance & Inspections", d: "MA-specific legal compliance, routine inspections, and lease enforcement handled end to end." },
  { icon: HomeIcon, t: "Rent Collection", d: "Automated online rent collection, deposits handled per MA law, and firm-but-fair delinquency management." },
  { icon: FileText, t: "Rental Market Analysis", d: "Data-driven pricing so your unit is never left on the table — or on the market too long." },
];

const steps = [
  { n: "01", t: "Consultation", d: "We learn your goals and assess your property — free and no obligation." },
  { n: "02", t: "Onboarding", d: "We handle the paperwork, inspections, pricing, and marketing." },
  { n: "03", t: "Placement", d: "We screen, show, and place a qualified tenant fast." },
  { n: "04", t: "Management", d: "We handle everything ongoing. You watch the returns roll in." },
];

export default function Services() {
  return (
    <>
      <Seo title="Services" description="Full-service residential property management: tenant screening, leasing, maintenance, financial reporting, and compliance across Massachusetts." />
      <PageHero
        overline="Full-service management"
        titleLines={["Comprehensive", "care for your", "investment."]}
        subtitle="One partner for the entire lifecycle of your rental — from pricing and placement to maintenance and month-end reporting."
      />

      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-6 py-20 lg:px-10 lg:py-28">
          <StaggerGroup className="grid gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-4">
            {services.map((s, i) => (
              <motion.div key={i} variants={staggerItem} className="group bg-background p-8 transition-colors hover:bg-muted lg:p-10">
                <s.icon className="h-8 w-8 text-accent" strokeWidth={1.4} />
                <h3 className="mt-6 font-serif text-2xl font-normal">{s.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
              </motion.div>
            ))}
          </StaggerGroup>
        </div>
      </section>

      <section className="border-b border-border bg-primary text-primary-foreground">
        <div className="mx-auto max-w-[1400px] px-6 py-20 lg:px-10 lg:py-32">
          <Reveal><span className="overline text-accent">How it works</span></Reveal>
          <Reveal delay={0.1}><h2 className="mt-5 max-w-2xl font-serif text-4xl font-light lg:text-5xl">From keys to cash flow in four steps.</h2></Reveal>
          <div className="mt-14 grid gap-px border border-primary-foreground/15 bg-primary-foreground/15 md:grid-cols-2 lg:grid-cols-4">
            {steps.map((s, i) => (
              <Reveal key={i} delay={i * 0.08} className="bg-primary p-8 lg:p-10">
                <span className="font-serif text-5xl font-light text-accent">{s.n}</span>
                <h3 className="mt-5 font-serif text-2xl">{s.t}</h3>
                <p className="mt-3 text-sm text-primary-foreground/70">{s.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
